// src/frontend/js/modern/lib/popup-lite.js

const popupInstances = new Map();
const visiblePopupStack = [];
let _cachedMaxZ = null;
let _cachedMaxZTime = 0;
const Z_INDEX_CACHE_TTL = 5000;

const FOCUSABLE_SELECTORS = [
	'a[href]',
	'area[href]',
	'input:not([disabled]):not([type="hidden"])',
	'select:not([disabled])',
	'textarea:not([disabled])',
	'button:not([disabled])',
	'iframe',
	'object',
	'embed',
	'*[tabindex]',
	'*[contenteditable]'
].join(', ');

let scrollbarWidth = null;

function getScrollbarWidth() {
	if (scrollbarWidth !== null) return scrollbarWidth;
	const parent = document.createElement('div');
	parent.style.cssText = 'width:50px;height:50px;overflow:auto;position:absolute;top:-9999px;left:-9999px;';
	const child = document.createElement('div');
	parent.appendChild(child);
	document.body.appendChild(parent);
	scrollbarWidth = parent.offsetWidth - child.offsetWidth;
	document.body.removeChild(parent);
	return scrollbarWidth;
}

function getDefaultOptions(popup) {
	return {
		absolute: false,
		color: '#333',
		opacity: '0.5',
		transition: 'all 0.3s',
		horizontal: popup.dataset.horizontal || 'center',
		vertical: popup.dataset.vertical || 'middle',
		scrolllock: true,
		background: true,
		escape: true,
		blur: true,
		keepfocus: true,
		focuselement: null,
		focusdelay: 50,
		onclose: null,
		onopen: null,
		beforeopen: null,
	};
}

function updateBodyState() {
	const visibleCount = [...popupInstances.values()].filter(inst => inst.visible).length;
	if (visibleCount > 0) {
		document.body.classList.add('wlfmc-with-popup');
		document.documentElement.classList.add('popup_visible');

		const scrollLockNeeded = [...popupInstances.values()].some(inst => inst.visible && inst.options.scrolllock);
		if (scrollLockNeeded) {
			const hasScrollableBody = document.body.scrollHeight > window.innerHeight;
			if (hasScrollableBody) {
				const sbw = getScrollbarWidth();
				const currentMarginRight = parseInt(getComputedStyle(document.body).marginRight, 10) || 0;
				document.body.style.marginRight = (currentMarginRight + sbw) + 'px';
			}
			document.body.style.overflow = 'hidden';
		}
	} else {
		document.body.classList.remove('wlfmc-with-popup');
		document.documentElement.classList.remove('popup_visible');
		document.body.style.overflow = '';
		document.body.style.marginRight = '';
	}
}

function trapFocus(popup, event) {
	if (event.key !== 'Tab' && event.keyCode !== 9) return;
	if (!popup) return;

	const focusableItems = Array.from(popup.querySelectorAll(FOCUSABLE_SELECTORS)).filter(
		el => el.offsetParent !== null
	);

	if (focusableItems.length === 0) {
		event.preventDefault();
		popup.focus();
		return;
	}

	const focusedItem = document.activeElement;
	const focusedIndex = focusableItems.indexOf(focusedItem);

	if (event.shiftKey) {
		if (focusedIndex === 0 || focusedIndex === -1) {
			event.preventDefault();
			focusableItems[focusableItems.length - 1].focus();
		}
	} else {
		if (focusedIndex === focusableItems.length - 1 || focusedIndex === -1) {
			event.preventDefault();
			focusableItems[0].focus();
		}
	}
}

function applyAriaAttributes(popup, visible) {
	popup.setAttribute('aria-hidden', String(!visible));
	popup.setAttribute('role', 'dialog');

	if (visible) {
		popup.setAttribute('aria-modal', 'true');
	} else {
		popup.removeAttribute('aria-modal');
	}

	if (!popup.getAttribute('aria-label') && !popup.getAttribute('aria-labelledby')) {
		const title = popup.querySelector('.wlfmc-popup-title');
		if (title) {
			if (!title.id) {
				title.id = popup.id + '_title';
			}
			popup.setAttribute('aria-labelledby', title.id);
		} else {
			popup.setAttribute('aria-label', 'Popup dialog');
		}
	}
}


function calculateMaxZIndex() {
	const now = Date.now();
	if (_cachedMaxZ !== null && (now - _cachedMaxZTime) < Z_INDEX_CACHE_TTL) {
		return _cachedMaxZ;
	}
	let maxZ = 0;
	const candidates = document.querySelectorAll(
		'[style*="z-index"], .wlfmc-popup, .popup_wrapper, [class*="modal"], [class*="overlay"], [class*="dropdown"]'
	);
	for (const el of candidates) {
		const z = getComputedStyle(el).zIndex;
		if (z !== 'auto') {
			const zInt = parseInt(z, 10);
			if (!isNaN(zInt) && zInt > maxZ) {
				maxZ = zInt;
			}
		}
	}
	_cachedMaxZ = maxZ;
	_cachedMaxZTime = now;
	return maxZ;
}

export function getOrCreate(popup, options = {}) {
	const id = popup.id;
	if (!id) return null;

	if (popupInstances.has(id)) {
		const existing = popupInstances.get(id);
		if (Object.keys(options).length > 0) {
			existing.options = { ...existing.options, ...options };
		}
		return existing;
	}

	const existingWrapper = document.getElementById(`${id}_wrapper`);
	if (existingWrapper) {
		const instance = {
			id,
			popup,
			wrapper: existingWrapper,
			background: document.getElementById(`${id}_background`),
			options: { ...getDefaultOptions(popup), ...options },
			visible: false,
			focusedElementBefore: null,
		};
		popupInstances.set(id, instance);
		applyAriaAttributes(popup, false);
		return instance;
	}

	const mergedOptions = { ...getDefaultOptions(popup), ...options };

	const useAutoZ = mergedOptions.autozindex === true;
	const maxZ = useAutoZ ? calculateMaxZIndex() : 0;
	const bgZ = useAutoZ ? maxZ + 1 : 100000;
	const wrapperZ = useAutoZ ? maxZ + 2 : 100001;

	const background = document.createElement('div');
	background.id = `${id}_background`;
	background.className = 'popup_background wlfmc-overlay';
	background.style.cssText = [
		'opacity:0',
		'visibility:hidden',
		`background-color:${mergedOptions.color}`,
		'position:fixed',
		'top:0', 'right:0', 'bottom:0', 'left:0',
		`z-index:${bgZ}`,
	].join(';');

	if (mergedOptions.transition) background.style.transition = mergedOptions.transition;

	const iOS = /(iPad|iPhone|iPod)/.test(navigator.userAgent);
	if (iOS) {
		background.style.cursor = 'pointer';
	}

	const wrapper = document.createElement('div');
	wrapper.id = `${id}_wrapper`;
	wrapper.className = 'popup_wrapper';
	wrapper.style.cssText = [
		'opacity:0',
		'visibility:hidden',
		'position:fixed',
		'width:100%', 'height:100%',
		'top:0', 'left:0',
		`text-align:${mergedOptions.horizontal}`,
		'overflow:auto',
		`z-index:${wrapperZ}`,
	].join(';');

	if (mergedOptions.transition) wrapper.style.transition = mergedOptions.transition;

	const align = document.createElement('div');
	align.className = 'popup_align';
	align.style.cssText = 'display:inline-block;vertical-align:middle;height:100%;';
	wrapper.appendChild(align);

	popup.classList.add('popup_content');
	popup.style.verticalAlign = mergedOptions.vertical;
	popup.style.position = 'relative';
	popup.style.textAlign = 'left';
	if (mergedOptions.transition) popup.style.transition = mergedOptions.transition;
	popup.style.outline = 'none';

	wrapper.appendChild(popup);
	document.body.appendChild(background);
	document.body.appendChild(wrapper);

	applyAriaAttributes(popup, false);

	const instance = {
		id,
		popup,
		wrapper,
		background,
		options: mergedOptions,
		visible: false,
		focusedElementBefore: null,
	};

	popupInstances.set(id, instance);
	return instance;
}

export function showPopup(popup) {
	const id = popup.id;
	if (!id) return;

	if (!popupInstances.has(id)) {
		getOrCreate(popup);
	}

	const instance = popupInstances.get(id);
	if (!instance || instance.visible) return;

	const { wrapper, background, options } = instance;

	if (typeof options.beforeopen === 'function') {
		options.beforeopen.call(popup);
	}

	instance.focusedElementBefore = document.activeElement;

	document.documentElement.classList.add('popup_visible', `popup_visible_${id}`);
	wrapper.classList.add('popup_wrapper_visible');

	setTimeout(() => {
		wrapper.style.visibility = 'visible';
		wrapper.style.opacity = '1';
		background.style.visibility = 'visible';
		background.style.opacity = options.opacity || '0.5';
	}, 20);

	if (options.transition) {
		const onTransitionEnd = () => {
			wrapper.removeEventListener('transitionend', onTransitionEnd);
			if (typeof options.opentransitionend === 'function') {
				options.opentransitionend.call(popup);
			}
		};
		wrapper.addEventListener('transitionend', onTransitionEnd);
	} else {
		if (typeof options.opentransitionend === 'function') {
			options.opentransitionend.call(popup);
		}
	}

	if (options.blur) {
		background.onclick = () => hidePopup(popup);
	}

	if (options.escape) {
		instance._escHandler = (e) => {
			if ((e.key === 'Escape' || e.keyCode === 27) &&
				visiblePopupStack[visiblePopupStack.length - 1] === id) {
				hidePopup(popup);
			}
		};
		document.addEventListener('keydown', instance._escHandler);
	}

	if (options.keepfocus) {
		instance._focusTrapHandler = (e) => {
			if (visiblePopupStack[visiblePopupStack.length - 1] === id) {
				trapFocus(popup, e);
			}
		};
		document.addEventListener('keydown', instance._focusTrapHandler);

		popup.setAttribute('tabindex', '-1');

		setTimeout(() => {
			if (options.focuselement === 'closebutton') {
				const closeBtn = popup.querySelector('.wlfmc-popup-close:first-of-type');
				if (closeBtn) closeBtn.focus();
			} else if (options.focuselement) {
				const target = document.querySelector(options.focuselement);
				if (target) target.focus();
			} else {
				popup.focus();
			}
		}, options.focusdelay || 50);
	}

	applyAriaAttributes(popup, true);

	visiblePopupStack.push(id);
	instance.visible = true;

	updateBodyState();

	if (typeof options.onopen === 'function') {
		options.onopen.call(popup);
	}
}

export function hidePopup(popup) {
	const id = popup.id;
	const instance = popupInstances.get(id);
	if (!instance || !instance.visible) return;

	const { wrapper, background, options } = instance;

	document.documentElement.classList.remove(`popup_visible_${id}`);
	wrapper.classList.remove('popup_wrapper_visible');

	wrapper.style.visibility = 'hidden';
	wrapper.style.opacity = '0';
	background.style.visibility = 'hidden';
	background.style.opacity = '0';

	if (instance._escHandler) {
		document.removeEventListener('keydown', instance._escHandler);
		instance._escHandler = null;
	}

	if (instance._focusTrapHandler) {
		document.removeEventListener('keydown', instance._focusTrapHandler);
		instance._focusTrapHandler = null;
	}

	background.onclick = null;

	applyAriaAttributes(popup, false);

	if (options.keepfocus && instance.focusedElementBefore) {
		const el = instance.focusedElementBefore;
		if (el && el.offsetParent !== null) {
			el.focus();
		}
	}
	instance.focusedElementBefore = null;

	const idx = visiblePopupStack.indexOf(id);
	if (idx > -1) visiblePopupStack.splice(idx, 1);
	instance.visible = false;

	updateBodyState();

	if (typeof options.onclose === 'function') {
		const fn = options.onclose;
		options.onclose = null;
		fn.call(popup);
	}
}

export function hideAllPopups() {
	const visible = [...popupInstances.values()].filter(inst => inst.visible);
	visible.reverse().forEach(inst => hidePopup(inst.popup));
}

export function isPopupVisible(popup) {
	const instance = popupInstances.get(popup.id);
	return instance ? instance.visible : false;
}

/**
 * Permanently destroy a popup and its overlay elements.
 * Use this instead of element.remove() when the popup will never
 * be shown again (e.g. after deleting a list).
 *
 * 1. Hides the popup if currently visible
 * 2. Detaches the popup node from the generated wrapper
 * 3. Removes wrapper + background from the DOM
 * 4. Cleans up the internal instance registry
 * 5. Restores body scroll/overflow state
 */
export function destroyPopup(popup) {
	const id = popup.id;
	if (!id) return;

	// Hide first so scroll-lock / body classes are released cleanly.
	const instance = popupInstances.get(id);
	if (instance && instance.visible) {
		hidePopup(popup);
	}

	const wrapper = document.getElementById(`${id}_wrapper`);
	const background = document.getElementById(`${id}_background`);

	// Detach the popup node before removing the wrapper,
	// so it can still be referenced without errors if needed.
	if (wrapper && wrapper.contains(popup)) {
		wrapper.removeChild(popup);
	}

	wrapper?.remove();
	background?.remove();

	// Remove the instance from the registry entirely.
	popupInstances.delete(id);

	// Purge from the visibility stack in case it leaked.
	const idx = visiblePopupStack.indexOf(id);
	if (idx > -1) visiblePopupStack.splice(idx, 1);

	updateBodyState();
}
